
// Ensure CartPerformance is available or create a fallback
if (typeof CartPerformance === 'undefined') {
  window.CartPerformance = {
    createStartingMarker: () => null,
    measureFromEvent: () => { },
    measureFromMarker: () => { },
    measure: (name, callback) => callback()
  };
}

const __WL_CONTINUE_STORAGE_KEY = '__wl_last_browsed_url';

function __wl_isCartLikePath(pathname) {
  if (typeof pathname !== 'string') return false;
  return /^\/(cart|checkout|account)/.test(pathname);
}

function __wl_storeLastBrowseUrl() {
  if (typeof window === 'undefined') return;
  if (__wl_isCartLikePath(window.location.pathname)) return;
  try {
    if (window.sessionStorage) {
      window.sessionStorage.setItem(__WL_CONTINUE_STORAGE_KEY, window.location.href);
    }
  } catch (_) { /* noop */ }
}

function __wl_getStoredContinueUrl() {
  if (typeof window === 'undefined') return '';
  try {
    if (!window.sessionStorage) return '';
    const raw = window.sessionStorage.getItem(__WL_CONTINUE_STORAGE_KEY) || '';
    if (!raw) return '';
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return '';
    if (__wl_isCartLikePath(url.pathname)) return '';
    return url.href;
  } catch (_) {
    return '';
  }
}

function __wl_resolveContinueTarget(fallbackHref) {
  const stored = __wl_getStoredContinueUrl();
  if (stored) return stored;

  try {
    if (document.referrer) {
      const refUrl = new URL(document.referrer, window.location.origin);
      if (refUrl.origin === window.location.origin && !__wl_isCartLikePath(refUrl.pathname)) {
        return refUrl.href;
      }
    }
  } catch (_) { /* noop */ }

  if (fallbackHref) return fallbackHref;
  try {
    if (window.routes && window.routes.all_products_collection_url) {
      return window.routes.all_products_collection_url;
    }
  } catch (_) { /* noop */ }
  return '/';
}

function __wl_bindContinueShoppingLinks() {
  if (typeof document === 'undefined') return;
  const links = document.querySelectorAll('[data-continue-shopping]');
  if (!links.length) return;

  links.forEach((link) => {
    if (link.dataset.continueShoppingBound === 'true') return;
    link.dataset.continueShoppingBound = 'true';

    const mode = link.dataset.continueShopping || 'page';
    link.addEventListener('click', (event) => {
      const fallback = link.getAttribute('href') || '/';
      if (mode === 'drawer') {
        event.preventDefault();
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.close === 'function') drawer.close();
        return;
      }

      event.preventDefault();
      const target = __wl_resolveContinueTarget(fallback);
      if (target) window.location.href = target;
    });
  });
}

let __wl_pruneServicesInFlight = null;
async function __wl_pruneOrphanServiceLines(trigger = 'unknown') {
  if (__wl_pruneServicesInFlight) return __wl_pruneServicesInFlight;

  __wl_pruneServicesInFlight = (async () => {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => {
        if (!r.ok) throw new Error(`cart.js fetch failed: ${r.status}`);
        return r.json();
      });

      const items = Array.isArray(cart?.items) ? cart.items : [];
      if (!items.length) return;

      const visibleKeys = new Set();
      items.forEach((item) => {
        const props = item?.properties || {};
        if (props._hidden_service === 'true') return;
        if (item?.key) visibleKeys.add(item.key);
      });

      const updates = {};
      items.forEach((item) => {
        const props = item?.properties || {};
        if (props._hidden_service !== 'true') return;
        const parentKey = props._parent_line_id;
        const hasParent = parentKey && visibleKeys.has(parentKey);
        if (!hasParent && item?.key) {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }
    } catch (err) {
      try {
        console.warn('[cart] prune services failed', trigger, err);
      } catch (_) { /* noop */ }
    }
  })().finally(() => {
    __wl_pruneServicesInFlight = null;
  });

  return __wl_pruneServicesInFlight;
}

if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    __wl_storeLastBrowseUrl();
    __wl_bindContinueShoppingLinks();
    __wl_pruneOrphanServiceLines('dom-ready');
  });
}

if (typeof subscribe === 'function') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (evt) => {
    __wl_pruneOrphanServiceLines(evt?.source || 'cart-update');
    setTimeout(__wl_bindContinueShoppingLinks, 0);
  });
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Always handle cart updates, even those originating from this component,
      // so we re-render after any server-side changes (e.g. linked service syncs).
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          // Fallback: also update header counts in case header isn't using cart-icon-bubble
          try {
            const bubbleSection = html.querySelector('#shopify-section-cart-icon-bubble');
            if (bubbleSection) {
              const count = this._extractCartCountFromSection(bubbleSection);
              if (typeof count === 'number') this._updateFallbackHeaderCounts(count);
            }
          } catch (e) { /* noop */ }
        })
        .then(() => {
          if (typeof __wl_bindContinueShoppingLinks === 'function') {
            __wl_bindContinueShoppingLinks();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      // Refresh all relevant cart sections (items, footer, icon bubble, live region)
      // using the sections JSON endpoint so totals are accurate after any server-side syncs.
      const sectionsToRequest = this.getSectionsToRender().map((s) => s.section).join(',');
      return fetch(`${routes.cart_url}?sections=${encodeURIComponent(sectionsToRequest)}`)
        .then((response) => response.json())
        .then((sectionsHtml) => {
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = document.getElementById(section.id);
            const htmlString = sectionsHtml[section.section];
            if (sectionElement && htmlString) {
              const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
              elementToReplace.innerHTML = this.getSectionInnerHTML(htmlString, section.selector);
            }
          });

          // Fallback: if #cart-icon-bubble anchor is missing, still update header/mobile counts
          try {
            const bubbleHtml = sectionsHtml['cart-icon-bubble'];
            if (bubbleHtml && !document.getElementById('cart-icon-bubble')) {
              const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
              const sectionRoot = doc.querySelector('#shopify-section-cart-icon-bubble') || doc.body;
              const count = this._extractCartCountFromSection(sectionRoot);
              if (typeof count === 'number') this._updateFallbackHeaderCounts(count);
            }
          } catch (e) { /* noop */ }
        })
        .then(() => {
          if (typeof __wl_bindContinueShoppingLinks === 'function') {
            __wl_bindContinueShoppingLinks();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const sections = [];

    // Only add main cart sections if they exist (for cart page)
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    // Always add cart icon bubble
    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section',
    });

    // Only add live region if it exists
    const liveRegion = document.getElementById('cart-live-region-text');
    if (liveRegion) {
      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }

    // Only add main cart footer if it exists (for cart page)
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }

  // Canonical: sync quantities of linked service lines to the parent
  async syncLinkedServiceLinesByKey(parentKey, parentQty) {
    try {
      const snapshot = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
      const items = Array.isArray(snapshot.items) ? snapshot.items : [];

      // Find all service lines linked to this parent (by _parent_line_id or bundle_group)
      const parent = items.find(it => it && it.key === parentKey);
      const bundle = parent && parent.properties ? parent.properties.bundle_group : null;

      const services = items.filter((it) => {
        const p = it.properties || {};
        const isService = p._hidden_service === 'true';
        const linkByParent = p._parent_line_id && p._parent_line_id === parentKey;
        const linkByBundle = bundle && p.bundle_group && p.bundle_group === bundle;
        return isService && (linkByParent || linkByBundle);
      });

      if (!services.length) return;

      // Decide target qty for each service; default = per-item mirror
      const updates = {};
      for (const s of services) {
        const p = s.properties || {};
        let newQty = parentQty;

        // Per-order only if explicitly flagged
        const perOrder =
          String(p._per_order).toLowerCase() === 'true' ||
          String(p.per_order).toLowerCase() === 'true' ||
          String(p.service_billing).toLowerCase() === 'order';

        if (perOrder) newQty = 1;

        // Optional fixed override
        if (p._fixed_quantity !== undefined && !isNaN(Number(p._fixed_quantity))) {
          newQty = Number(p._fixed_quantity);
        }

        if (s.key) updates[s.key] = newQty;
      }

      let updatedCart = null;
      if (Object.keys(updates).length) {
        const resp = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates })
        });
        try { updatedCart = await resp.json(); } catch (_) { /* fallback handled by caller */ }
      }
      return updatedCart;
    } catch (e) {
      console.error('[cart] syncLinkedServiceLinesByKey error', e);
      return null;
    }
  }


  updateQuantity(line, quantity, event, name, variantId) {
    // Capture the line's DOM key before any re-render happens so we can
    // identify linked service items even if the server response no longer
    // contains the removed line.
    const rowBefore =
      document.getElementById(`CartItem-${line}`) ||
      document.getElementById(`CartDrawer-Item-${line}`);
    const domKeyBefore = rowBefore && rowBefore.dataset ? rowBefore.dataset.key : null;

    this.enableLoading(line);

    const preRemoveServices = async (parentKey) => {
      try {
        if (!parentKey || quantity !== 0) return;
        const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
        const updates = {};
        (cart.items || []).forEach((it) => {
          const p = it.properties || {};
          if (p._hidden_service === 'true' && p._parent_line_id === parentKey && it.key) {
            updates[it.key] = 0;
          }
        });
        if (Object.keys(updates).length) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates })
          });
        }
      } catch (_) { }
    };

    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    const changeLine = (targetLine) => {
      const body = JSON.stringify({
        line: targetLine,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });
      return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.text())
        .then((state) => {
          let parsedState;
          try {
            parsedState = JSON.parse(state);
          } catch (e) {
            // If parsing fails (e.g., transient HTML response), trigger a full refresh instead of surfacing an error.
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
            return;
          }


          CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
            const quantityElement =
              document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
            const items = document.querySelectorAll('.cart-item');

            if (parsedState.errors) {
              if (quantityElement) {
                quantityElement.value = quantityElement.getAttribute('value');
              }
              this.updateLiveRegions(line, parsedState.errors);
              return;
            }

            // Snapshot current drawer subtotal to avoid flashing stale values after render
            const drawerSubtotalSelector = '#CartDrawer .totals__subtotal-value, #CartDrawer [data-subtotal]';
            const oldSubtotalEl = document.querySelector(drawerSubtotalSelector);
            const prevSubtotalText = oldSubtotalEl ? oldSubtotalEl.textContent : null;

            // Render updated sections from server response
            this.classList.toggle('is-empty', parsedState.item_count === 0);
            const cartDrawerWrapper = document.querySelector('cart-drawer');
            const cartFooter = document.getElementById('main-cart-footer');

            if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
            if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

            this.getSectionsToRender().forEach((section) => {
              const sectionElement = document.getElementById(section.id);
              if (sectionElement && parsedState.sections[section.section]) {
                const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
                elementToReplace.innerHTML = this.getSectionInnerHTML(
                  parsedState.sections[section.section],
                  section.selector
                );
              }
            });
            if (typeof __wl_bindContinueShoppingLinks === 'function') {
              __wl_bindContinueShoppingLinks();
            }

            // After render, keep subtotal at previous value and mark loading to avoid flash
            const newSubtotalEl = document.querySelector(drawerSubtotalSelector);
            if (newSubtotalEl && prevSubtotalText != null) {
              newSubtotalEl.textContent = prevSubtotalText;
              newSubtotalEl.setAttribute('data-loading', 'true');
            }
            const cartDrawerRoot = document.getElementById('CartDrawer');
            if (cartDrawerRoot) cartDrawerRoot.setAttribute('data-syncing', 'true');

            const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
            let message = '';
            if (
              quantityElement &&
              items.length === parsedState.items.length &&
              updatedValue !== parseInt(quantityElement.value)
            ) {
              if (typeof updatedValue === 'undefined') {
                message = window.cartStrings.error;
              } else {
                message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
              }
            }
            this.updateLiveRegions(line, message);

            const lineItem =
              document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
            if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
              cartDrawerWrapper
                ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
                : lineItem.querySelector(`[name="${name}"]`).focus();
            } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
            } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
            }

            // Step 2: Derive parent key/qty, sync services, then refresh subtotal from /cart.js, then publish
            const changedItem = Array.isArray(parsedState.items) ? parsedState.items[line - 1] : null;
            let parentKey = changedItem && changedItem.key ? changedItem.key : domKeyBefore;
            const parentQty = typeof quantity === 'number'
              ? quantity
              : (changedItem && typeof changedItem.quantity === 'number' ? changedItem.quantity : undefined);

            (async () => {
              let updatedCart = null;
              if (parentKey && typeof parentQty === 'number') {
                updatedCart = await this.syncLinkedServiceLinesByKey(parentKey, parentQty);
              }

              try {
                const cart = updatedCart || await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
                const subtotalCents = typeof cart.total_price === 'number'
                  ? cart.total_price
                  : (Array.isArray(cart.items) ? cart.items.reduce((sum, it) => sum + (it.final_line_price || 0), 0) : 0);

                const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
                const subtotalText = (subtotalCents / 100).toLocaleString(undefined, { style: 'currency', currency });

                const subtotalEl =
                  document.querySelector('#CartDrawer .totals__subtotal-value') ||
                  document.querySelector('#CartDrawer [data-subtotal]');

                if (subtotalEl) {
                  subtotalEl.textContent = subtotalText;
                  subtotalEl.removeAttribute('data-loading');
                }
                const cartDrawerRoot = document.getElementById('CartDrawer');
                if (cartDrawerRoot) cartDrawerRoot.removeAttribute('data-syncing');
              } catch (e) {
                console.warn('[cart] failed to refresh subtotal from /cart.js', e);
              }

              // Publish after service sync and subtotal refresh
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
            })();
          });

          CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

          // Also update fallback header/mobile counts
          try {
            const bubbleHtml = parsedState.sections && parsedState.sections['cart-icon-bubble'];
            if (bubbleHtml) {
              const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
              const sectionRoot = doc.querySelector('#shopify-section-cart-icon-bubble') || doc.body;
              const count = this._extractCartCountFromSection(sectionRoot);
              if (typeof count === 'number') this._updateFallbackHeaderCounts(count);
            } else if (typeof parsedState.item_count === 'number') {
              // Fallback to total item_count if section not present
              this._updateFallbackHeaderCounts(parsedState.item_count);
            }
          } catch (e) { /* noop */ }

        })
        .catch((e) => {
          // Avoid flashing the generic error banner; log and force a refresh instead.
          try { console.error(e); } catch (_) { }
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        })
        .finally(() => {
          this.disableLoading(line);
        });
    };

    // If removing, pre-remove services linked to this item, then proceed
    if (quantity === 0 && domKeyBefore) {
      preRemoveServices(domKeyBefore).finally(() => {
        // After service removals, the parent's line index may have shifted.
        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            let resolved = null;
            (cart.items || []).forEach((it, idx) => {
              if (it && it.key === domKeyBefore) resolved = idx + 1;
            });
            return changeLine(resolved || line);
          })
          .catch(() => changeLine(line));
      });
    } else {
      changeLine(line);
    }
  }

  // Extracts numeric count from the cart-icon-bubble section DOM
  _extractCartCountFromSection(sectionRoot) {
    if (!sectionRoot) return null;
    // Preferred: explicit aria-hidden number inside bubble
    const ariaNum = sectionRoot.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    if (ariaNum) {
      const n = parseInt(ariaNum.textContent.replace(/[^0-9]/g, ''), 10);
      if (!isNaN(n)) return n;
    }
    // Fallback: parse any digits from the visually hidden localized text
    const sr = sectionRoot.querySelector('.cart-count-bubble .visually-hidden');
    if (sr) {
      const m = sr.textContent && sr.textContent.match(/[0-9]+/);
      if (m) return parseInt(m[0], 10);
    }
    return null;
  }

  // Updates any legacy header/mobile count elements when cart-icon-bubble anchor is absent
  _updateFallbackHeaderCounts(count) {
    try {
      const n = typeof count === 'number' ? count : 0;

      // Helper to set or remove count elements
      const setCount = (container, className) => {
        if (!container) return;
        let el = container.querySelector(`.${className}`);
        if (n > 0) {
          if (!el) {
            el = document.createElement('span');
            el.className = className;
            container.appendChild(el);
          }
          el.textContent = n > 99 ? '99+' : String(n);
          el.style.display = '';
        } else if (el) {
          // Remove element entirely to mirror liquid conditional
          el.remove();
        }
      };

      // Desktop header
      setCount(document.querySelector('.header-right .cart-area .cart-link'), 'cart-count');
      // Mobile header
      setCount(document.querySelector('.mobile-header .mobile-cart-link'), 'cart-count');
      // Mega menu mobile drawer button
      setCount(document.querySelector('.mega-menu-new-cart-button'), 'mega-menu-new-cart-count');
    } catch (_) { /* noop */ }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = message;
      }
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const element = parsedHTML.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => {
                CartPerformance.measureFromEvent('note-update:user-action', event);
              });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
